import React, { useMemo, useState } from 'react';
import './DemoEnroll.css';

/* =========================================================
   Enroll from the feedback link (demo families). Lives inside ParentFeedback (/feedback/:token).
   Steps: package -> class times (or "later") -> how to pay -> confirm.
   Then: the place is held (pending), payment details + "send me the registration details on WhatsApp"
   (the details land in their WhatsApp chat with us, ready for when they pay),
   and "choose the times now" if they chose later.
   ========================================================= */
const API = `${process.env.REACT_APP_API_BASE_URL}/api/feedback`;
const WA_NUMBER = process.env.REACT_APP_MOBILE_NUMBER1;
const PAY_TO = { name: 'Nadim Nasrallah', number: '03 315 951', intl: '+961 3 315 951' };

const THEME_NAME = {
  Starter: 'Starter Rocket',
  Advanced: 'Space Coder',
  Premium: 'Galaxy Builder',
  'Robotics Advanced': 'Robotics Explorer',
  'Mastery Starter': 'Code Pioneer',
  'Mastery Premium': 'Quantum Architect',
};
const ORDER = ['Starter', 'Advanced', 'Premium', 'Mastery Starter', 'Mastery Premium', 'Robotics Advanced'];
const groupOf = (name) => (name.includes('Robotics') ? 'robotics' : name.includes('Mastery') ? 'mastery' : 'standard');
const money = (v) => {
  const n = Number(v);
  return Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`;
};
const BEIRUT = 'Asia/Beirut';
const myZone = () => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || BEIRUT;
  } catch {
    return BEIRUT;
  }
};

const T = {
  en: {
    enrollTitle: (c) => (c ? `Enroll ${c} 🚀` : 'Enroll now 🚀'),
    enrollIntro: 'Three quick steps. You pay after, by the way you prefer.',
    offer: (pct, day) => `🎁 Special offer: ${pct}% off our coding packages, until ${day}.`,
    stepPackage: '1. Choose a package',
    stepTimes: '2. Class times',
    stepPay: '3. How would you like to pay?',
    groups: { standard: 'Standard courses', mastery: 'Mastery courses', robotics: 'Robotics (kit delivered home)' },
    sessions: (n) => `${n} sessions`,
    perWeek: (n) => `${n} ${n === 1 ? 'class' : 'classes'} a week`,
    popular: 'Most popular',
    withTeacher: (t) => `With ${t}, the same teacher as the free class.`,
    pickN: (n) => `Choose ${n} ${n === 1 ? 'time' : 'times'} a week (shown in your time).`,
    lebanon: 'Lebanon',
    later: "I'll choose the times later",
    laterNote: 'Times are only held once you choose them. Popular times can fill up.',
    noTimes: 'We’ll find the best times with you on WhatsApp.',
    prefTitle: '2. When suits you best?',
    prefIntro: 'Tell us what works for you, and we’ll confirm the exact times on WhatsApp.',
    prefDays: 'Days that work for you',
    prefTimes: 'Time of day',
    prefPerWeek: 'Classes per week',
    prefDayNames: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
    prefTimeNames: { morning: 'Morning (9–12)', afternoon: 'Afternoon (12–4)', afterschool: 'After school (4–7)', evening: 'Evening (7–9)' },
    prefPerWeekN: (n) => `${n} ${n === 1 ? 'class' : 'classes'}`,
    needPrefs: 'Please choose at least one day and one time of day.',
    methods: {
      whish: 'Whish (Whish to Whish)',
      omt: 'OMT (wallet to wallet)',
      bob: 'BOB Finance',
      wu: 'Western Union',
      card: 'Credit card (we send you a payment link)',
    },
    confirm: 'Reserve my place',
    confirming: 'Reserving…',
    back: 'Back',
    next: 'Next',
    needPkg: 'Please choose a package.',
    needTimes: (n) => `Please choose ${n} ${n === 1 ? 'time' : 'times'}, or “I'll choose the times later”.`,
    needMethod: 'Please choose how you’d like to pay.',
    taken: 'Sorry, one of these times was just taken. Please choose again.',
    error: 'Something went wrong. Please try again.',
    heldTitle: (c) => `${c ? `${c}'s` : 'Your'} place is reserved! 🎉`,
    heldUntil: (d) => `We’re holding it until ${d}. It’s confirmed once we receive the payment.`,
    paidTitle: 'Payment received, welcome to RoboMind! 🎉',
    paidText: 'The login details were sent to you. See you in class!',
    stoppedTitle: 'This reservation has ended',
    stoppedText: 'No problem! Message us on WhatsApp whenever you’re ready, and we’ll find a place for your child.',
    summary: 'Your choice',
    times: 'Class times',
    timesLater: 'Not chosen yet',
    chooseNow: 'Choose the times now',
    saveTimes: 'Save these times',
    saving: 'Saving…',
    payTitle: 'How to pay',
    amount: 'Amount',
    toNumber: 'Number',
    toName: 'Name',
    country: 'Country',
    countryVal: 'Lebanon',
    phone: 'Phone',
    wuNote: 'Then send us the MTCN (the transfer number) with the receipt.',
    cardNote: 'We’ll send you a secure card payment link on WhatsApp very soon.',
    copy: 'Copy',
    copied: 'Copied!',
    regBtn: 'Send me the registration details on WhatsApp',
    regHint: 'The details stay in your WhatsApp chat with us, so you can pay whenever you’re ready, then send the receipt in the same chat.',
    regMsg: ({ child, pkg, amount, times, method, pay }) =>
      [
        `Hi! I want to enroll ${child || 'my child'} at RoboMind 😊 Here are the registration details:`,
        '',
        `📦 Package: ${pkg}${amount ? ` (${amount})` : ''}`,
        `🕒 Class times: ${times}`,
        `💳 Payment: ${method}`,
        ...pay,
        '',
        'I’ll send the receipt here once I pay.',
      ].join('\n'),
    payLines: {
      number: (n, name) => [`   Number: ${n}`, `   Name: ${name}`],
      wu: (name, phone) => [`   Name: ${name}`, '   Country: Lebanon', `   Phone: ${phone}`, '   (I’ll send the MTCN with the receipt)'],
      card: ['   Please send me the card payment link.'],
    },
    lebTime: '(Lebanon time)',
    help: 'Questions? Message us on WhatsApp',
    helpText: (c) => `Hi! I have a question about enrolling ${c || 'my child'}.`,
    cta: (c) => (c ? `Enroll ${c}` : 'Enroll now'),
  },
  ar: {
    enrollTitle: (c) => (c ? `سجّلوا ${c} 🚀` : 'سجّلوا الآن 🚀'),
    enrollIntro: 'ثلاث خطوات سريعة. تدفعون بعدها بالطريقة التي تناسبكم.',
    offer: (pct, day) => `🎁 عرض خاص: خصم ${pct}% على باقات البرمجة، حتى ${day}.`,
    stepPackage: '١. اختاروا الباقة',
    stepTimes: '٢. أوقات الحصص',
    stepPay: '٣. كيف تفضّلون الدفع؟',
    groups: { standard: 'الدورات العادية', mastery: 'دورات Mastery', robotics: 'الروبوتات (العدّة تصل إلى المنزل)' },
    sessions: (n) => `${n} حصة`,
    perWeek: (n) => (n === 1 ? 'حصة واحدة في الأسبوع' : `${n} حصص في الأسبوع`),
    popular: 'الأكثر طلباً',
    withTeacher: (t) => `مع ${t}، نفس الأستاذ/ة في الحصة المجانية.`,
    pickN: (n) => (n === 1 ? 'اختاروا وقتاً واحداً في الأسبوع (بتوقيتكم).' : `اختاروا ${n} أوقات في الأسبوع (بتوقيتكم).`),
    lebanon: 'لبنان',
    later: 'سأختار الأوقات لاحقاً',
    laterNote: 'لا نحجز الأوقات إلا بعد اختيارها. الأوقات المطلوبة قد تمتلئ.',
    noTimes: 'سنجد معكم أفضل الأوقات عبر واتساب.',
    prefTitle: '٢. ما الأوقات التي تناسبكم؟',
    prefIntro: 'أخبرونا بما يناسبكم، ونؤكد معكم الأوقات بالضبط عبر واتساب.',
    prefDays: 'الأيام المناسبة',
    prefTimes: 'الوقت من النهار',
    prefPerWeek: 'عدد الحصص في الأسبوع',
    prefDayNames: ['الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'],
    prefTimeNames: { morning: 'صباحاً (٩–١٢)', afternoon: 'بعد الظهر (١٢–٤)', afterschool: 'بعد المدرسة (٤–٧)', evening: 'مساءً (٧–٩)' },
    prefPerWeekN: (n) => (n === 1 ? 'حصة واحدة' : n === 2 ? 'حصتان' : `${n} حصص`),
    needPrefs: 'الرجاء اختيار يوم واحد على الأقل ووقت واحد من النهار.',
    methods: {
      whish: 'Whish (من Whish إلى Whish)',
      omt: 'OMT (من محفظة إلى محفظة)',
      bob: 'BOB Finance',
      wu: 'Western Union',
      card: 'بطاقة ائتمان (نرسل لكم رابط الدفع)',
    },
    confirm: 'احجزوا المكان',
    confirming: 'جارٍ الحجز…',
    back: 'رجوع',
    next: 'التالي',
    needPkg: 'الرجاء اختيار باقة.',
    needTimes: (n) => (n === 1 ? 'الرجاء اختيار وقت، أو «سأختار الأوقات لاحقاً».' : `الرجاء اختيار ${n} أوقات، أو «سأختار الأوقات لاحقاً».`),
    needMethod: 'الرجاء اختيار طريقة الدفع.',
    taken: 'عذراً، أحد هذه الأوقات حُجز للتو. الرجاء الاختيار مجدداً.',
    error: 'حدث خطأ. الرجاء المحاولة مجدداً.',
    heldTitle: (c) => (c ? `تم حجز مكان ${c}! 🎉` : 'تم حجز المكان! 🎉'),
    heldUntil: (d) => `نحتفظ به حتى ${d}. يتأكّد التسجيل عند استلام الدفعة.`,
    paidTitle: 'تم استلام الدفعة، أهلاً بكم في RoboMind! 🎉',
    paidText: 'أرسلنا لكم معلومات الدخول. نراكم في الصف!',
    stoppedTitle: 'انتهى هذا الحجز',
    stoppedText: 'لا مشكلة! راسلونا على واتساب عندما تكونون جاهزين، وسنجد مكاناً لطفلكم.',
    summary: 'اختياركم',
    times: 'أوقات الحصص',
    timesLater: 'لم تُختر بعد',
    chooseNow: 'اختاروا الأوقات الآن',
    saveTimes: 'حفظ هذه الأوقات',
    saving: 'جارٍ الحفظ…',
    payTitle: 'طريقة الدفع',
    amount: 'المبلغ',
    toNumber: 'الرقم',
    toName: 'الاسم',
    country: 'البلد',
    countryVal: 'لبنان',
    phone: 'الهاتف',
    wuNote: 'ثم أرسلوا لنا رقم الحوالة (MTCN) مع الإيصال.',
    cardNote: 'سنرسل لكم رابط دفع آمن بالبطاقة على واتساب قريباً جداً.',
    copy: 'نسخ',
    copied: 'تم النسخ!',
    regBtn: 'أرسلوا لي تفاصيل التسجيل على واتساب',
    regHint: 'تبقى التفاصيل في محادثتكم معنا على واتساب، فتدفعون متى كنتم جاهزين، ثم ترسلون الإيصال في نفس المحادثة.',
    regMsg: ({ child, pkg, amount, times, method, pay }) =>
      [
        `مرحباً! أريد تسجيل ${child || 'طفلي'} في RoboMind 😊 هذه تفاصيل التسجيل:`,
        '',
        `📦 الباقة: ${pkg}${amount ? ` (${amount})` : ''}`,
        `🕒 أوقات الحصص: ${times}`,
        `💳 الدفع: ${method}`,
        ...pay,
        '',
        'سأرسل الإيصال هنا بعد الدفع.',
      ].join('\n'),
    payLines: {
      number: (n, name) => [`   الرقم: ${n}`, `   الاسم: ${name}`],
      wu: (name, phone) => [`   الاسم: ${name}`, '   البلد: لبنان', `   الهاتف: ${phone}`, '   (سأرسل رقم الحوالة MTCN مع الإيصال)'],
      card: ['   أرجو إرسال رابط الدفع بالبطاقة.'],
    },
    lebTime: '(بتوقيت لبنان)',
    help: 'لديكم سؤال؟ راسلونا على واتساب',
    helpText: (c) => `مرحباً! لدي سؤال عن تسجيل ${c || 'طفلي'}.`,
    cta: (c) => (c ? `سجّلوا ${c}` : 'سجّلوا الآن'),
  },
};

const waUrl = (text) =>
  `https://api.whatsapp.com/send/?phone=${WA_NUMBER}&text=${encodeURIComponent(text)}&type=phone_number&app_absent=0`;

/* Weekly times, grouped by day, in the family's own time zone */
const TimePicker = ({ slots, chosen, max, onToggle, lang, t }) => {
  const zone = myZone();
  const abroad = zone !== BEIRUT;
  const loc = lang === 'ar' ? 'ar-LB' : 'en-US';
  const days = useMemo(() => {
    const map = new Map();
    slots.forEach((s) => {
      const d = new Date(s.at);
      const key = d.toLocaleDateString('en-US', { weekday: 'long', timeZone: zone });
      if (!map.has(key)) map.set(key, { label: d.toLocaleDateString(loc, { weekday: 'long', timeZone: zone }), order: d.getTime(), items: [] });
      map.get(key).items.push(s);
    });
    return [...map.values()].sort((a, b) => a.items[0].day - b.items[0].day || a.order - b.order); // Monday first
  }, [slots, zone, loc]);

  return (
    <div className="rmden-days">
      {days.map((d) => (
        <div key={d.label} className="rmden-day">
          <b>{d.label}</b>
          <div className="rmden-times">
            {d.items
              .sort((a, b) => new Date(a.at) - new Date(b.at))
              .map((s) => {
                const k = `${s.day}-${s.slot}`;
                const on = chosen.includes(k);
                const at = new Date(s.at);
                return (
                  <button
                    key={k}
                    type="button"
                    className={`rmden-time ${on ? 'is-on' : ''}`}
                    aria-pressed={on}
                    disabled={!on && chosen.length >= max}
                    onClick={() => onToggle(k)}
                  >
                    {at.toLocaleTimeString(loc, { hour: 'numeric', minute: '2-digit', timeZone: zone })}
                    {abroad && <small>{`${t.lebanon} ${at.toLocaleTimeString(loc, { hour: 'numeric', minute: '2-digit', timeZone: BEIRUT })}`}</small>}
                  </button>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
};

const Copy = ({ value, t }) => {
  const [done, setDone] = useState(false);
  return (
    <button
      type="button"
      className="rmden-copy"
      onClick={() => {
        try {
          navigator.clipboard.writeText(value.replace(/\s/g, ''));
          setDone(true);
          setTimeout(() => setDone(false), 1500);
        } catch {
          /* ignore */
        }
      }}
    >
      {done ? t.copied : t.copy}
    </button>
  );
};

/* Payment details for the method they chose */
const PayDetails = ({ method, amount, t }) => (
  <div className="rmden-pay">
    <p className="rmden-pay-title">
      {t.payTitle}: <b>{t.methods[method]}</b>
    </p>
    {amount != null && (
      <p className="rmden-row">
        <span>{t.amount}</span>
        <b>{money(amount)}</b>
      </p>
    )}
    {method === 'card' ? (
      <p className="rmden-note">{t.cardNote}</p>
    ) : method === 'wu' ? (
      <>
        <p className="rmden-row">
          <span>{t.toName}</span>
          <b>{PAY_TO.name}</b>
        </p>
        <p className="rmden-row">
          <span>{t.country}</span>
          <b>{t.countryVal}</b>
        </p>
        <p className="rmden-row">
          <span>{t.phone}</span>
          <b dir="ltr">{PAY_TO.intl}</b>
          <Copy value={PAY_TO.intl} t={t} />
        </p>
        <p className="rmden-note">{t.wuNote}</p>
      </>
    ) : (
      <>
        <p className="rmden-row">
          <span>{t.toNumber}</span>
          <b dir="ltr">{PAY_TO.number}</b>
          <Copy value={PAY_TO.number} t={t} />
        </p>
        <p className="rmden-row">
          <span>{t.toName}</span>
          <b>{PAY_TO.name}</b>
        </p>
      </>
    )}
  </div>
);

/* data = GET /:token/enroll ; onState(state) when it changes */
const DemoEnroll = ({ token, data, lang, onState, onClose }) => {
  const t = T[lang];
  const child = data.child;
  const packages = useMemo(
    () => [...(data.packages || [])].sort((a, b) => (ORDER.indexOf(a.name) + 1 || 99) - (ORDER.indexOf(b.name) + 1 || 99)),
    [data.packages]
  );
  const [slots, setSlots] = useState(data.slots || []);
  const [step, setStep] = useState(1);
  const [pkgId, setPkgId] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [later, setLater] = useState(false);
  const [method, setMethod] = useState('');
  // no teacher times to pick (e.g. a win-back offer): the family says what suits them
  const [prefs, setPrefs] = useState({ days: [], times: [], perWeek: null });
  const flip = (key, v) => setPrefs((p) => ({ ...p, [key]: p[key].includes(v) ? p[key].filter((x) => x !== v) : [...p[key], v] }));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const state = data.state;

  const pkg = packages.find((p) => p.id === pkgId);
  const need = Math.min(pkg?.perWeek || 1, slots.length || 1);
  const toggle = (k) => setChosen((c) => (c.includes(k) ? c.filter((x) => x !== k) : [...c, k]));
  const asSlots = (list) => list.map((k) => ({ day: Number(k.split('-')[0]), slot: Number(k.split('-')[1]) }));

  const post = async (path, body) => {
    const res = await fetch(`${API}/${encodeURIComponent(token)}/${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    const json = await res.json().catch(() => ({}));
    return { res, json };
  };

  const goNext = () => {
    setError('');
    if (step === 1 && !pkg) return setError(t.needPkg);
    if (step === 2 && !later && slots.length && chosen.length < need) return setError(t.needTimes(need));
    if (step === 2 && !slots.length && (!prefs.days.length || !prefs.times.length)) return setError(t.needPrefs);
    setStep((s) => s + 1);
    return undefined;
  };

  const confirm = async () => {
    if (!method) return setError(t.needMethod);
    setBusy(true);
    setError('');
    try {
      const noTimes = later || !slots.length;
      const { res, json } = await post('enroll', {
        packageId: pkgId,
        method,
        later: noTimes,
        slots: noTimes ? [] : asSlots(chosen),
        prefs: slots.length ? null : { days: prefs.days, times: prefs.times, perWeek: prefs.perWeek || pkg?.perWeek || 1 },
      });
      if (res.status === 409 && json.error === 'taken') {
        setSlots(json.slots || []);
        setChosen([]);
        setStep(2);
        setError(t.taken);
      } else if (!res.ok || !json.success) {
        setError(t.error);
      } else {
        onState(json.state);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } catch {
      setError(t.error);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  /* ---------- after enrolling: held / paid / ended ---------- */
  const [picking, setPicking] = useState(false);
  const saveTimes = async () => {
    if (chosen.length < Math.min(state.perWeek || 1, slots.length || 1)) return setError(t.needTimes(Math.min(state.perWeek || 1, slots.length || 1)));
    setBusy(true);
    setError('');
    try {
      const { res, json } = await post('slots', { slots: asSlots(chosen) });
      if (res.status === 409 && json.error === 'taken') {
        setSlots(json.slots || []);
        setChosen([]);
        setError(t.taken);
      } else if (!res.ok || !json.success) setError(t.error);
      else {
        setPicking(false);
        setChosen([]);
        onState(json.state);
      }
    } catch {
      setError(t.error);
    } finally {
      setBusy(false);
    }
    return undefined;
  };

  if (state) {
    const until = state.until
      ? new Date(`${state.until}T12:00:00`).toLocaleDateString(lang === 'ar' ? 'ar-LB' : 'en-US', { weekday: 'long', month: 'long', day: 'numeric' })
      : '';
    const pkgName = THEME_NAME[state.packageName] || state.packageName || '';
    const amount = state.amount != null ? money(state.amount) : '';
    if (state.status === 'stopped') {
      return (
        <div className="rmpfb-end">
          <span className="rmpfb-end-icon" aria-hidden="true">🕊️</span>
          <h1>{t.stoppedTitle}</h1>
          <p>{t.stoppedText}</p>
          <a className="rmpfb-btn" href={waUrl(t.helpText(child))} target="_blank" rel="noopener noreferrer">
            {t.help}
          </a>
        </div>
      );
    }
    if (state.status === 'active') {
      return (
        <div className="rmpfb-end">
          <span className="rmpfb-end-icon" aria-hidden="true">🎉</span>
          <h1>{t.paidTitle}</h1>
          <p>{t.paidText}</p>
        </div>
      );
    }
    return (
      <div className="rmden">
        <div className="rmpfb-end rmden-held">
          <span className="rmpfb-end-icon" aria-hidden="true">🎉</span>
          <h1>{t.heldTitle(child)}</h1>
          {until && <p>{t.heldUntil(until)}</p>}
        </div>

        <div className="rmden-box">
          <p className="rmden-box-title">{t.summary}</p>
          <p className="rmden-row">
            <span>{pkgName}</span>
            <b>{amount}</b>
          </p>
          <p className="rmden-row">
            <span>{t.times}</span>
            <b>{state.slots?.length ? state.slots.join(' · ') : t.timesLater}</b>
          </p>
          {!state.slots?.length && slots.length > 0 && !picking && (
            <button type="button" className="rmpfb-btn rmpfb-btn--ghost rmden-wide" onClick={() => setPicking(true)}>
              {t.chooseNow}
            </button>
          )}
          {picking && (
            <>
              <p className="rmden-hint">{t.pickN(Math.min(state.perWeek || 1, slots.length))}</p>
              <TimePicker slots={slots} chosen={chosen} max={Math.min(state.perWeek || 1, slots.length)} onToggle={toggle} lang={lang} t={t} />
              {error && <p className="rmpfb-error" role="alert">{error}</p>}
              <button type="button" className="rmpfb-btn rmden-wide" onClick={saveTimes} disabled={busy}>
                {busy ? t.saving : t.saveTimes}
              </button>
            </>
          )}
        </div>

        {state.method && <PayDetails method={state.method} amount={state.amount} t={t} />}

        <a
          className="rmpfb-btn rmden-wa"
          href={waUrl(
            t.regMsg({
              child,
              pkg: pkgName,
              amount,
              times: state.slots?.length ? `${state.slots.join(' · ')} ${t.lebTime}` : t.timesLater,
              method: t.methods[state.method] || '',
              pay:
                state.method === 'card'
                  ? t.payLines.card
                  : state.method === 'wu'
                  ? t.payLines.wu(PAY_TO.name, PAY_TO.intl)
                  : t.payLines.number(PAY_TO.number, PAY_TO.name),
            })
          )}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t.regBtn}
        </a>
        <p className="rmden-hint rmden-center">{t.regHint}</p>
      </div>
    );
  }

  /* ---------- the 3 steps ---------- */
  const groups = ['standard', 'mastery', 'robotics']
    .map((g) => ({ g, list: packages.filter((p) => groupOf(p.name) === g) }))
    .filter((x) => x.list.length);

  return (
    <div className="rmden">
      <h1 className="rmpfb-title">{t.enrollTitle(child)}</h1>
      <p className="rmpfb-muted">{t.enrollIntro}</p>
      {data.offer && !state && (
        <p className="rmden-offer" role="note">
          {t.offer(
            data.offer.percent,
            new Date(`${data.offer.endsOn}T12:00:00Z`).toLocaleDateString(lang === 'ar' ? 'ar-LB' : 'en-US', {
              weekday: 'long',
              month: 'long',
              day: 'numeric',
              timeZone: 'UTC',
            })
          )}
        </p>
      )}

      <ol className="rmden-steps" aria-hidden="true">
        {[1, 2, 3].map((n) => (
          <li key={n} className={n === step ? 'is-on' : n < step ? 'is-done' : ''} />
        ))}
      </ol>

      {step === 1 && (
        <section>
          <h2 className="rmden-h2">{t.stepPackage}</h2>
          {groups.map(({ g, list }) => (
            <div key={g} className="rmden-group">
              <p className="rmden-group-title">{t.groups[g]}</p>
              {list.map((p) => {
                const hasOffer = p.offerPrice > 0 && p.offerPrice < p.price;
                return (
                  <label key={p.id} className={`rmden-pkg ${pkgId === p.id ? 'is-on' : ''}`}>
                    <input type="radio" name="pkg" checked={pkgId === p.id} onChange={() => { setPkgId(p.id); setChosen([]); }} />
                    <span className="rmden-pkg-main">
                      <b>{THEME_NAME[p.name] || p.name}</b>
                      <small>
                        {t.sessions(p.sessions)} · {t.perWeek(p.perWeek)}
                        {p.name === 'Premium' ? ` · ${t.popular}` : ''}
                      </small>
                    </span>
                    <span className="rmden-price">
                      {hasOffer && <s>{money(p.price)}</s>}
                      <b>{money(p.amount)}</b>
                    </span>
                  </label>
                );
              })}
            </div>
          ))}
        </section>
      )}

      {step === 2 && (
        <section>
          <h2 className="rmden-h2">{slots.length ? t.stepTimes : t.prefTitle}</h2>
          {data.teacher && slots.length > 0 && <p className="rmden-hint">{t.withTeacher(data.teacher.name)}</p>}
          {slots.length > 0 ? (
            <>
              {!later && (
                <>
                  <p className="rmden-hint">{t.pickN(need)}</p>
                  <TimePicker slots={slots} chosen={chosen} max={need} onToggle={toggle} lang={lang} t={t} />
                </>
              )}
              <label className={`rmden-later ${later ? 'is-on' : ''}`}>
                <input type="checkbox" checked={later} onChange={(e) => setLater(e.target.checked)} />
                <span>
                  <b>{t.later}</b>
                  <small>{t.laterNote}</small>
                </span>
              </label>
            </>
          ) : (
            <div className="rmden-prefs">
              <p className="rmden-hint">{t.prefIntro}</p>
              <p className="rmden-group-title">{t.prefDays}</p>
              <div className="rmden-chips">
                {t.prefDayNames.map((name, i) => (
                  <button
                    key={name}
                    type="button"
                    className={prefs.days.includes(i + 1) ? 'is-on' : ''}
                    aria-pressed={prefs.days.includes(i + 1)}
                    onClick={() => flip('days', i + 1)}
                  >
                    {name}
                  </button>
                ))}
              </div>
              <p className="rmden-group-title">{t.prefTimes}</p>
              <div className="rmden-chips">
                {Object.entries(t.prefTimeNames).map(([k, name]) => (
                  <button key={k} type="button" className={prefs.times.includes(k) ? 'is-on' : ''} aria-pressed={prefs.times.includes(k)} onClick={() => flip('times', k)}>
                    {name}
                  </button>
                ))}
              </div>
              <p className="rmden-group-title">{t.prefPerWeek}</p>
              <div className="rmden-chips">
                {[1, 2, 3].map((n) => {
                  const on = (prefs.perWeek || pkg?.perWeek || 1) === n;
                  return (
                    <button key={n} type="button" className={on ? 'is-on' : ''} aria-pressed={on} onClick={() => setPrefs((p) => ({ ...p, perWeek: n }))}>
                      {t.prefPerWeekN(n)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </section>
      )}

      {step === 3 && (
        <section>
          <h2 className="rmden-h2">{t.stepPay}</h2>
          <div className="rmden-methods">
            {(data.methods || []).map((m) => (
              <label key={m} className={`rmden-method ${method === m ? 'is-on' : ''}`}>
                <input type="radio" name="method" checked={method === m} onChange={() => setMethod(m)} />
                {t.methods[m]}
              </label>
            ))}
          </div>
          {method && <PayDetails method={method} amount={pkg?.amount} t={t} />}
        </section>
      )}

      {error && <p className="rmpfb-error" role="alert">{error}</p>}

      <div className="rmden-nav">
        {step > 1 ? (
          <button type="button" className="rmpfb-btn rmpfb-btn--ghost" onClick={() => { setError(''); setStep((s) => s - 1); }} disabled={busy}>
            {t.back}
          </button>
        ) : (
          onClose && (
            <button type="button" className="rmpfb-btn rmpfb-btn--ghost" onClick={onClose}>
              {t.back}
            </button>
          )
        )}
        {step < 3 ? (
          <button type="button" className="rmpfb-btn" onClick={goNext}>
            {t.next}
          </button>
        ) : (
          <button type="button" className="rmpfb-btn" onClick={confirm} disabled={busy}>
            {busy ? t.confirming : t.confirm}
          </button>
        )}
      </div>

      <a className="rmden-help" href={waUrl(t.helpText(child))} target="_blank" rel="noopener noreferrer">
        {t.help}
      </a>
    </div>
  );
};

export { T as ENROLL_TEXT };
export default DemoEnroll;
